import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import PageBackground from '../components/PageBackground';
import { useLanguage } from '../contexts/LanguageContext';
import apiService from '../services/api';
import './LiveDarshan.css';

const withPlayerParams = (streamUrl = '') => {
  try {
    const url = new URL(streamUrl);
    url.searchParams.set('autoplay', '1');
    url.searchParams.set('mute', '0');
    url.searchParams.set('modestbranding', '1');
    return url.toString();
  } catch (error) {
    const separator = streamUrl.includes('?') ? '&' : '?';
    return `${streamUrl}${separator}autoplay=1&mute=0&modestbranding=1`;
  }
};

const LiveDarshan = () => {
  const { t } = useLanguage();
  const [selectedTemple, setSelectedTemple] = useState(null);
  const [temples, setTemples] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  const imageUrl = '/images/bg.avif';

  const getTempleName = (temple) => t(`liveDarshan.temples.${temple.id}.name`, {}, temple.name);
  const getTempleLocation = (temple) => t(`liveDarshan.temples.${temple.id}.location`, {}, temple.location);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  useEffect(() => {
    document.body.classList.add('live-darshan-theme');
    return () => {
      document.body.classList.remove('live-darshan-theme');
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    const fetchShrines = async () => {
      try {
        const response = await apiService.getBhaktiDarshanShrines();
        const shrines = Array.isArray(response.data) ? response.data : [];
        if (isMounted) {
          setTemples(shrines);
          setError('');
        }
      } catch (err) {
        if (isMounted) {
          setTemples([]);
          setError(err.message || 'Unable to load live darshan list.');
        }
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    fetchShrines();

    return () => {
      isMounted = false;
    };
  }, []);

  // Close player when Escape key is pressed
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setSelectedTemple(null);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);


  return (
    <div className="live-darshan-page">
      <PageBackground imageUrl={imageUrl} />

      <div className="darshan-page-header">
        <div className="container-header">
          <h1>{t('liveDarshan.header.title')}</h1>
          <p>{t('liveDarshan.header.subtitle')}</p>
        </div>
      </div>

      <div className="darshan-main-content">
        <div className="container">
          {isLoading ? (
            <div className="darshan-state-message">Loading live darshan...</div>
          ) : (
            <>
              {error ? <div className="darshan-state-message subtle">{error}</div> : null}
              {temples.length === 0 ? (
                <div className="darshan-state-message">No live darshan shrines are active right now.</div>
              ) : (
                <motion.div
                  className="temples-grid"
                  initial="hidden"
                  animate="visible"
                  variants={{
                    visible: { transition: { staggerChildren: 0.1 } }
                  }}
                >
                  {temples.map((temple) => (
                    <motion.div
                      key={temple.id}
                      className="temple-card"
                      onClick={() => setSelectedTemple(temple)}
                      variants={{
                        hidden: { opacity: 0, y: 30 },
                        visible: { opacity: 1, y: 0 }
                      }}
                      whileHover={{ scale: 1.05 }}
                      layoutId={`card-${temple.id}`}
                    >
                      <img src={temple.thumbnail} alt={getTempleName(temple)} className="card-thumbnail" loading="lazy" decoding="async" />
                      <div className="card-overlay"></div>
                      <div className="card-content">
                        <h3>{getTempleName(temple)}</h3>
                        <p>{getTempleLocation(temple)}</p>
                      </div>
                      {/* <div className="live-indicator">
                        <span className="live-dot"></span>{t('liveDarshan.liveBadge')}
                      </div> */}
                    </motion.div>
                  ))}
                </motion.div>
              )}
            </>
          )}
        </div>
      </div>

      {/* --- MODAL FOR THE VIDEO PLAYER --- */}
      <AnimatePresence>
        {selectedTemple && (
          <motion.div
            className="stream-modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div className="stream-modal-content" layoutId={`card-${selectedTemple.id}`}>
              <div className="video-container">
                <iframe
                  src={withPlayerParams(selectedTemple.streamUrl)}
                  title={t('liveDarshan.iframeTitle', { name: getTempleName(selectedTemple) }, `Bhakti Darshan - ${getTempleName(selectedTemple)}`)}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                ></iframe>
              </div>
              <div className="stream-info">
                <h2>{getTempleName(selectedTemple)}</h2>
                <p>{getTempleLocation(selectedTemple)}</p>
              </div>
            </motion.div>
            <button className="close-stream-btn" onClick={() => setSelectedTemple(null)}>
              <i className="fas fa-times"></i>
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default LiveDarshan;
